import { Button } from "@/components/ui/button";
import { CalendarDialog } from "@/components/CalendarDialog";
import { ArrowRight, CheckCircle } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export function HeroSection() {
  return <section id="hero-section" className="relative pt-24 sm:pt-28 lg:pt-32 pb-12 sm:pb-16 lg:pb-20 overflow-hidden">
      {/* Premium layered background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-[hsl(100_25%_97%)] to-[hsl(98_30%_95%/0.6)]" />
      
      {/* Soft radial glow behind heading */}
      <div className="absolute inset-0 bg-gradient-hero-glow pointer-events-none" />

      {/* Decorative ambient elements - smaller on mobile */}
      <div className="absolute top-1/4 left-0 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] bg-primary/[0.04] rounded-full blur-[60px] sm:blur-[100px] -translate-x-1/2" />
      <div className="absolute bottom-1/4 right-0 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] bg-primary/[0.04] rounded-full blur-[60px] sm:blur-[100px] translate-x-1/2" />
      
      {/* Subtle grid texture */}
      <div className="absolute inset-0 grid-pattern opacity-30" />

      <div className="w-full max-w-[600px] sm:max-w-[700px] lg:max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center">
          {/* Badge - Premium styling */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1]
        }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background border border-border/80 mb-4 sm:mb-5" style={{
          boxShadow: '0 2px 12px -3px rgba(0,0,0,0.08)'
        }}>
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse flex-shrink-0" />
            <span className="text-[11px] sm:text-xs text-primary tracking-wide font-bold">
              Digital Growth Systems for Australian Trades
            </span>
          </motion.div>

          {/* Main Headline - Bolder on desktop */}
          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.08,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-[2rem] sm:text-[2.5rem] md:text-[3rem] lg:text-[3.75rem] font-extrabold text-foreground leading-[1.12] mb-3 sm:mb-4 lg:mb-5 tracking-[-0.025em]">
            <span className="block">The Digital Growth System</span>
            <span className="block">
              Built for <span className="text-primary">Trades, Construction</span>
            </span>
            <span className="block text-primary">& Home Services</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.14,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-[0.9375rem] sm:text-lg md:text-xl lg:text-2xl text-foreground/80 font-semibold leading-[1.4] mb-3 lg:mb-4 tracking-[-0.01em] max-w-[520px] lg:max-w-[680px] mx-auto">
            Your workmanship speaks for itself — your digital presence should too.
          </motion.p>

          {/* Body copy */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="hidden sm:block text-[0.9375rem] sm:text-base lg:text-lg text-muted-foreground leading-[1.7] max-w-[520px] lg:max-w-[640px] mx-auto mb-3 lg:mb-4">
            We help trade and construction businesses get found, build trust, and win more high-value work through a modern growth system designed specifically for your industry.
          </motion.p>

          {/* Mini-Subheading */}
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.26,
          ease: [0.22, 1, 0.36, 1]
        }} className="text-[0.8125rem] sm:text-[0.9375rem] lg:text-base font-semibold text-foreground mb-5">
            This isn't about random leads. This is{" "}
            <span className="text-primary font-bold">reliable, predictable growth</span> — every single week.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.32,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex flex-col sm:flex-row items-center justify-center gap-2.5 sm:gap-3 mb-5 sm:mb-6">
            <CalendarDialog buttonVariant="default" buttonSize="lg" buttonClassName="hero-cta-primary w-full sm:w-auto min-w-[240px] sm:min-w-[260px] h-11 sm:h-12 text-sm sm:text-[15px] font-bold rounded-xl" />
            <Button variant="outline" size="lg" asChild className="group hero-cta-secondary w-full sm:w-auto min-w-[180px] h-10 sm:h-11 text-xs sm:text-sm font-semibold rounded-xl bg-background border-border/80 hover:bg-muted/50">
              <Link to="/services">
                See Our Services
                <ArrowRight className="ml-2 w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.38,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex flex-wrap items-center justify-center gap-x-4 sm:gap-x-8 gap-y-1.5 text-sm sm:text-lg text-muted-foreground">
          {["Systems", "Growth", "Results"].map((item, index) => <div key={index} className="flex items-center gap-2 sm:gap-2.5">
                <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-primary" strokeWidth={2.5} />
                </div>
                <span className="font-medium">{item}</span>
              </div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}